import React from 'react';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import establishmentService from '../../services/establishmentService';

const ReviewSubmitStep = ({ formData, emailVerified, companyNameAvailable }) => {
    // Get establishment type label
    const getEstablishmentTypeLabel = (type) => {
        const typeLabels = {
            'individual': 'Individual',
            'proprietorship': 'Proprietorship',
            'partnership': 'Partnership',
            'llp': 'Limited Liability Partnership (LLP)',
            'private_limited': 'Private Limited Company',
            'public_limited': 'Public Limited Company',
            'one_man_company': 'One Man Company (OMC)',
            'society': 'Society',
            'trust': 'Trust',
            'nbfc': 'Non-Banking Financial Company (NBFC)',
            'bank': 'Bank',
            'mfi': 'Microfinance Institution (MFI)'
        };
        return typeLabels[type] || type;
    };

    // Get establishment fields for display
    const getEstablishmentFields = () => {
        if (!formData.type_of_establishment) return [];

        const fields = establishmentService.getFieldsForType(formData.type_of_establishment);
        return fields.filter(field => formData[field.name]);
    };

    return (
        <div className="review-submit-step p-fluid">
            <div className="mb-3 text-center">
                <h3 className="text-900 text-xl font-semibold mb-1">
                    <i className="pi pi-check-circle mr-2"></i>
                    Review Your Information
                </h3>
                <p className="text-600 text-sm">Please review all details carefully before submitting</p>
            </div>

            {/* Company Information Section */}
            <Card className="mb-3">
                <div className="flex align-items-center mb-3">
                    <i className="pi pi-building text-primary text-xl mr-2"></i>
                    <h4 className="text-base font-semibold text-900 m-0">
                        Company Information
                    </h4>
                </div>
                <Divider className="mt-0 mb-3" />

                <div className="grid">
                    <div className="col-12">
                        <div className="mb-2">
                            <label className="text-600 text-xs block mb-1">Company Name</label>
                            <div className="flex align-items-center gap-2">
                                <span className="text-900 font-semibold text-sm">
                                    {formData.company_name}
                                </span>
                                {companyNameAvailable === true && (
                                    <Tag value="Available" severity="success" icon="pi pi-check" />
                                )}
                            </div>
                        </div>
                    </div>

                    {formData.address && (
                        <div className="col-12">
                            <div className="mb-2">
                                <label className="text-600 text-xs block mb-1">Company Address</label>
                                <div className="text-900 font-medium text-sm" style={{ whiteSpace: 'pre-line' }}>
                                    {formData.address}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            {/* Establishment Details Section */}
            {formData.type_of_establishment && (
                <Card className="mb-3">
                    <div className="flex align-items-center mb-3">
                        <i className="pi pi-sitemap text-primary text-xl mr-2"></i>
                        <h4 className="text-base font-semibold text-900 m-0">
                            Establishment Details
                        </h4>
                    </div>
                    <Divider className="mt-0 mb-3" />

                    <div className="grid">
                        <div className="col-12">
                            <div className="mb-2">
                                <label className="text-600 text-xs block mb-1">Type of Establishment</label>
                                <div className="text-900 font-semibold text-sm">
                                    {getEstablishmentTypeLabel(formData.type_of_establishment)}
                                </div>
                            </div>
                        </div>

                        {getEstablishmentFields().map((field) => (
                            <div className="col-12 md:col-6" key={field.name}>
                                <div className="mb-2">
                                    <label className="text-600 text-xs block mb-1">{field.label}</label>
                                    <div className="text-900 font-medium text-sm">
                                        {formData[field.name]}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* GST Information */}
                        {formData.has_gst_certificate && formData.gst_certificate_number && (
                            <div className="col-12">
                                <Divider />
                                <div className="mb-2">
                                    <label className="text-600 text-xs block mb-1">GST Registration</label>
                                    <div className="text-900 font-medium font-mono text-sm">
                                        {formData.gst_certificate_number}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Bank Details */}
                        {(formData.bank_account_name || formData.account_number) && (
                            <>
                                <div className="col-12">
                                    <Divider />
                                    <label className="text-600 text-xs block mb-2 font-semibold">Bank Account Details</label>
                                </div>

                                {formData.bank_account_name && (
                                    <div className="col-12 md:col-6">
                                        <div className="mb-2">
                                            <label className="text-600 text-xs block mb-1">Account Name</label>
                                            <div className="text-900 font-medium text-sm">
                                                {formData.bank_account_name}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {formData.account_number && (
                                    <div className="col-12 md:col-6">
                                        <div className="mb-2">
                                            <label className="text-600 text-xs block mb-1">Account Number</label>
                                            <div className="text-900 font-medium font-mono text-sm">
                                                {formData.account_number}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {formData.account_type && (
                                    <div className="col-12 md:col-4">
                                        <div className="mb-2">
                                            <label className="text-600 text-xs block mb-1">Account Type</label>
                                            <div className="text-900 font-medium text-capitalize text-sm">
                                                {formData.account_type.replace('_', ' ')}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {formData.ifsc_code && (
                                    <div className="col-12 md:col-4">
                                        <div className="mb-2">
                                            <label className="text-600 text-xs block mb-1">IFSC Code</label>
                                            <div className="text-900 font-medium font-mono text-sm">
                                                {formData.ifsc_code}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {formData.bank_name && (
                                    <div className="col-12 md:col-4">
                                        <div className="mb-2">
                                            <label className="text-600 text-xs block mb-1">Bank Name</label>
                                            <div className="text-900 font-medium text-sm">
                                                {formData.bank_name}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Card>
            )}

            {/* Personal Information Section */}
            <Card className="mb-3">
                <div className="flex align-items-center mb-3">
                    <i className="pi pi-user text-primary text-xl mr-2"></i>
                    <h4 className="text-base font-semibold text-900 m-0">
                        Owner / Admin Details
                    </h4>
                </div>
                <Divider className="mt-0 mb-3" />

                <div className="grid">
                    <div className="col-12 md:col-6">
                        <div className="mb-2">
                            <label className="text-600 text-xs block mb-1">Full Name</label>
                            <div className="text-900 font-semibold text-sm">
                                {formData.first_name} {formData.last_name}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="mb-2">
                            <label className="text-600 text-xs block mb-1">Email Address</label>
                            <div className="flex align-items-center gap-2">
                                <span className="text-900 font-medium text-sm">{formData.email}</span>
                                {emailVerified && (
                                    <Tag value="Verified" severity="success" icon="pi pi-check" />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="mb-2">
                            <label className="text-600 text-xs block mb-1">Mobile Number</label>
                            <div className="text-900 font-medium text-sm">
                                +91 {formData.mobile_number}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="mb-2">
                            <label className="text-600 text-xs block mb-1">Password</label>
                            <div className="text-900 font-medium text-sm">
                                ••••••••
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Important Notice */}
            <Card className="bg-blue-50 border-blue-200" style={{ borderLeft: '4px solid var(--primary-color)' }}>
                <div className="flex align-items-start gap-2">
                    <i className="pi pi-info-circle text-primary text-xl"></i>
                    <div>
                        <h5 className="m-0 mb-2 text-primary font-semibold text-sm">Important Information</h5>
                        <ul className="m-0 pl-3 text-700 line-height-3 text-sm">
                            <li className="mb-1">You will be assigned as the <strong>Company Administrator</strong> with full access</li>
                            <li className="mb-1">You can add more users and assign roles after registration</li>
                            <li className="mb-1">All company and establishment details can be updated from your profile</li>
                            <li className="mb-1">Your account will be activated immediately upon submission</li>
                            <li>An email with login credentials will be sent to <strong>{formData.email}</strong></li>
                        </ul>
                    </div>
                </div>
            </Card>

            {/* Review Reminder */}
            <div className="mt-3 p-2 surface-100 border-round text-center">
                <p className="m-0 text-600 text-sm">
                    <i className="pi pi-exclamation-triangle mr-2 text-orange-500"></i>
                    Please ensure all information is correct before submitting. You can go back to edit if needed.
                </p>
            </div>
        </div>
    );
};

export default ReviewSubmitStep;
